import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';

import { cn } from '@/marketing/lib/utils';
import { Input } from '@/marketing/components/ui/input';
import { useDebounce } from '@/marketing/hooks/useDebounce';
import type { DiscoveryFilters, PaginationState } from '@/marketing/models/influencer';
import { MAX_VOUCHER_BATCH_SIZE, type VoucherCandidate } from '@/marketing/models/voucher';
import { INFLUENCER_QUERIES } from '@/marketing/infrastructure/influencer/utils/queries';
import { VOUCHER_QUERIES } from '@/marketing/infrastructure/influencer/voucher/utils/queries';
import { buildVoucherCandidates } from '@/marketing/lib/voucherCandidates';
import { formatNumber } from '@/marketing/lib/formatters';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 20;
// Wide enough that every code held by a handle matching the search term comes back in one page.
const ISSUED_CODE_LOOKUP_LIMIT = 200;

const EMPTY_FILTERS: Omit<DiscoveryFilters, 'search'> = {
  notSelected: false,
  minFollowers: null,
  engRateMin: null,
  countryCodes: [],
  languages: [],
  hasEmail: null,
  platform: '',
};

const SEARCH_PAGINATION: PaginationState = { pageIndex: 0, pageSize: SEARCH_PAGE_SIZE };

interface InfluencerPickerProps {
  selected: VoucherCandidate[];
  onChange: (selected: VoucherCandidate[]) => void;
}

export function InfluencerPicker({ selected, onChange }: InfluencerPickerProps) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const hasQuery = debouncedQuery.trim().length > 0;

  const searchQuery = useQuery({
    ...INFLUENCER_QUERIES.search({ ...EMPTY_FILTERS, search: debouncedQuery.trim() }, SEARCH_PAGINATION),
    enabled: hasQuery,
  });
  const issuedQuery = useQuery({
    ...VOUCHER_QUERIES.list({ search: debouncedQuery.trim(), limit: ISSUED_CODE_LOOKUP_LIMIT, offset: 0 }),
    enabled: hasQuery,
  });

  const candidates = useMemo(
    () =>
      buildVoucherCandidates({
        influencers: searchQuery.data?.data ?? [],
        issuedVouchers: issuedQuery.data?.codes ?? [],
      }),
    [searchQuery.data, issuedQuery.data],
  );

  const isSelected = (candidate: VoucherCandidate) =>
    selected.some((entry) => entry.influencerId === candidate.influencerId);

  const toggle = (candidate: VoucherCandidate) => {
    if (candidate.existingCode) {
      return;
    }
    if (isSelected(candidate)) {
      onChange(selected.filter((entry) => entry.influencerId !== candidate.influencerId));
      return;
    }
    if (selected.length >= MAX_VOUCHER_BATCH_SIZE) {
      return;
    }
    onChange([...selected, candidate]);
  };

  const remove = (influencerId: string) => {
    onChange(selected.filter((entry) => entry.influencerId !== influencerId));
  };

  return (
    <div>
      {selected.length > 0 && (
        <div className="tw-mb-2 tw-flex tw-flex-wrap tw-gap-1.5">
          {selected.map((entry) => (
            <span
              key={entry.influencerId}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-border tw-bg-muted tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium"
            >
              @{entry.handle}
              <button
                type="button"
                aria-label={`Remove @${entry.handle}`}
                onClick={() => remove(entry.influencerId)}
                className="tw-text-muted-foreground hover:tw-text-foreground"
              >
                <X className="tw-h-3 tw-w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by handle or name…" />

      {hasQuery && (
        <div className="tw-mt-1.5 tw-max-h-[180px] tw-overflow-y-auto tw-rounded-md tw-border tw-border-border">
          {searchQuery.isFetching && !searchQuery.data && (
            <div className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-muted-foreground">Searching…</div>
          )}
          {searchQuery.data && candidates.length === 0 && (
            <div className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-muted-foreground">No creators found.</div>
          )}
          {candidates.map((candidate) => {
            const disabled = candidate.existingCode !== null;
            return (
              <button
                key={candidate.influencerId}
                type="button"
                disabled={disabled}
                onClick={() => toggle(candidate)}
                className={cn(
                  'tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-border tw-px-3 tw-py-2 tw-text-left last:tw-border-b-0',
                  disabled ? 'tw-opacity-50' : 'hover:tw-bg-muted',
                )}
              >
                <span>
                  <span className="tw-text-sm tw-font-semibold">@{candidate.handle}</span>
                  <span className="tw-ml-2 tw-text-xs tw-text-muted-foreground">
                    {candidate.platform} · {candidate.displayName} · {formatNumber(candidate.followersCount)} followers
                  </span>
                </span>
                <span className="tw-shrink-0 tw-text-xs tw-text-muted-foreground">
                  {disabled ? `has ${candidate.existingCode}` : isSelected(candidate) ? 'Selected' : 'Add'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
